/**
 * Store Locator — view.js
 *
 * Depends on:
 *  - mapbox-gl  (enqueued by render.php, loaded before this script)
 *  - gsap + ScrollTrigger  (enqueued globally by the theme)
 *  - Google Maps JS API   (loaded dynamically; window.storeLocatorReady is the callback)
 */

document.addEventListener( 'DOMContentLoaded', () => {

    const wrapper = document.querySelector( '.wp-block-jormi-blocks-store-locator' );
    if ( ! wrapper ) return;

    // ── Read config from data attributes ─────────────────────────────────────

    const locationsData = JSON.parse( wrapper.dataset.locations || '[]' );
    const mapboxToken   = wrapper.dataset.mapboxToken;
    const gmapsKey      = wrapper.dataset.gmapsKey;

    // ── State ─────────────────────────────────────────────────────────────────

    let map;
    const markers       = [];
    let isInitialLoad   = true;

    // ── Elements ──────────────────────────────────────────────────────────────

    const mapEl         = document.getElementById( 'locator-map' );
    const mapOuterEl    = document.getElementById( 'locator-map-outer' );
    const listEl        = document.getElementById( 'locator-list' );
    const countEl       = wrapper.querySelector( '.store-locator__count' );
    const autocompleteInput = document.getElementById( 'locator-autocomplete' );
    const geoBtn        = document.getElementById( 'locator-geolocate' );

    // ── Mapbox init ───────────────────────────────────────────────────────────

    function initMap() {
        mapboxgl.accessToken = mapboxToken;

        map = new mapboxgl.Map( {
            container:  'locator-map',
            projection: 'mercator',
            style:      'mapbox://styles/mapbox/light-v11',
            center:     [ 0, 0 ],
            zoom:       0,
        } );

        map.addControl( new mapboxgl.NavigationControl() );

        map.on( 'load', () => {
            createMarkers();
            map.on( 'zoomend', checkVisibleMarkers );
            map.on( 'moveend', checkVisibleMarkers );
        } );

        revealMap();
    }

    // ── Markers ───────────────────────────────────────────────────────────────

    function createMarkers() {
        locationsData.forEach( ( location ) => {
            const popup = new mapboxgl.Popup( { offset: 12 } )
                .setHTML( buildPopupHTML( location ) );

            const marker = new mapboxgl.Marker()
                .setLngLat( location.coordinates )
                .setPopup( popup )
                .addTo( map );

            markers.push( marker );
        } );

        updateCount( locationsData.length );
    }

    function buildPopupHTML( location ) {
        return `
            <h5 class="store-locator__popup-title">${ escapeHTML( location.title ) }</h5>
            ${ location.address ? `<p class="store-locator__popup-address">${ escapeHTML( location.address ) }</p>` : '' }
            ${ location.phone   ? `<a href="tel:${ escapeHTML( location.phone ) }" class="store-locator__popup-link">${ escapeHTML( location.phone ) }</a>` : '' }
            ${ location.email   ? `<a href="mailto:${ escapeHTML( location.email ) }" class="store-locator__popup-link">${ escapeHTML( location.email ) }</a>` : '' }
        `;
    }

    // ── List rendering ────────────────────────────────────────────────────────

    function renderList( locations ) {
        listEl.innerHTML = locations.map( buildListItemHTML ).join( '' );
        updateCount( locations.length );

        if ( typeof gsap !== 'undefined' ) {
            gsap.fromTo(
                listEl.querySelectorAll( '.store-locator__item' ),
                { opacity: 0, y: 6 },
                { opacity: 1, y: 0, duration: 0.2, stagger: 0.05, ease: 'power1.out' }
            );
        }
    }

    function buildListItemHTML( location ) {
        return `
            <li class="store-locator__item"
                data-lat="${ location.coordinates[1] }"
                data-lng="${ location.coordinates[0] }">
                <h5 class="store-locator__item-title">${ escapeHTML( location.title ) }</h5>
                ${ location.address ? `<span class="store-locator__item-address">${ escapeHTML( location.address ) }</span>` : '' }
                ${ location.email   ? `<a href="mailto:${ escapeHTML( location.email ) }" class="store-locator__item-link">${ escapeHTML( location.email ) }</a>` : '' }
                ${ location.phone   ? `<a href="tel:${ escapeHTML( location.phone ) }" class="store-locator__item-link">${ escapeHTML( location.phone ) }</a>` : '' }
            </li>
        `;
    }

    function updateCount( count ) {
        if ( ! countEl ) return;
        countEl.textContent = `${ count } location${ count !== 1 ? 's' : '' } found`;
    }

    // ── Map bounds filter ─────────────────────────────────────────────────────

    function checkVisibleMarkers() {
        if ( isInitialLoad ) {
            isInitialLoad = false;
            return;
        }

        const bounds  = map.getBounds();
        const visible = locationsData.filter( ( _, i ) =>
            bounds.contains( markers[ i ].getLngLat() )
        );

        renderList( visible );
    }

    // ── Map reveal ────────────────────────────────────────────────────────────

    function revealMap() {
        if ( mapOuterEl ) mapOuterEl.classList.remove( 'is-hidden' );
        if ( typeof gsap !== 'undefined' ) {
            gsap.fromTo( mapEl, { opacity: 0 }, { opacity: 1, duration: 1 } );
        }
    }

    // ── Google Places autocomplete ────────────────────────────────────────────

    function initAutocomplete() {
        if ( ! autocompleteInput || typeof google === 'undefined' ) return;

        // Prevent form submit on Enter
        autocompleteInput.addEventListener( 'keydown', ( e ) => {
            if ( e.key === 'Enter' ) e.preventDefault();
        } );

        const autocomplete = new google.maps.places.Autocomplete( autocompleteInput, {
            fields: [ 'place_id', 'geometry', 'name' ],
        } );

        autocomplete.addListener( 'place_changed', () => {
            const place = autocomplete.getPlace();
            if ( place.geometry ) {
                map.flyTo( {
                    center: [
                        place.geometry.location.lng(),
                        place.geometry.location.lat(),
                    ],
                    zoom: 6,
                } );
            }
        } );
    }

    // ── Geolocation ───────────────────────────────────────────────────────────

    function handleGeolocate() {
        if ( ! navigator.geolocation ) {
            alert( 'Geolocation is not supported by your browser. Please enter a location manually.' );
            return;
        }

        geoBtn.disabled = true;
        geoBtn.setAttribute( 'aria-busy', 'true' );

        navigator.geolocation.getCurrentPosition(
            ( position ) => {
                const { latitude, longitude } = position.coords;

                // Reverse-geocode to fill the search input
                fetch(
                    `https://maps.googleapis.com/maps/api/geocode/json?latlng=${ latitude },${ longitude }&key=${ gmapsKey }`
                )
                    .then( ( r ) => r.json() )
                    .then( ( data ) => {
                        if ( autocompleteInput && data.results?.[0] ) {
                            autocompleteInput.value = data.results[0].formatted_address;
                        }
                        map.flyTo( { center: [ longitude, latitude ], zoom: 6 } );
                        revealMap();
                    } )
                    .catch( () => {
                        map.flyTo( { center: [ longitude, latitude ], zoom: 6 } );
                        revealMap();
                    } )
                    .finally( () => {
                        geoBtn.disabled = false;
                        geoBtn.removeAttribute( 'aria-busy' );
                    } );
            },
            ( error ) => {
                console.error( 'Geolocation error:', error );
                alert( 'Unable to retrieve your location. Please check your browser settings.' );
                geoBtn.disabled = false;
                geoBtn.removeAttribute( 'aria-busy' );
            }
        );
    }

    // ── Google Maps script loader ─────────────────────────────────────────────
    //
    // Google Maps uses a global callback to signal readiness.
    // We namespace it to avoid conflicts with other blocks or plugins.

    window.storeLocatorReady = function () {
        initAutocomplete();

        // If Mapbox is already loaded, init now; otherwise poll until ready.
        waitForMapbox( initMap );
    };

    function waitForMapbox( callback ) {
        if ( typeof mapboxgl !== 'undefined' ) {
            callback();
        } else {
            setTimeout( () => waitForMapbox( callback ), 50 );
        }
    }

    // Load Google Maps API
    const gmScript    = document.createElement( 'script' );
    gmScript.src      = `https://maps.googleapis.com/maps/api/js?key=${ gmapsKey }&libraries=places&callback=storeLocatorReady`;
    gmScript.async    = true;
    gmScript.defer    = true;
    document.head.appendChild( gmScript );

    // ── Event listeners ───────────────────────────────────────────────────────

    if ( geoBtn ) {
        geoBtn.addEventListener( 'click', handleGeolocate );
    }

    // ── Utility ───────────────────────────────────────────────────────────────

    function escapeHTML( str ) {
        if ( ! str ) return '';
        return String( str )
            .replace( /&/g,  '&amp;' )
            .replace( /</g,  '&lt;' )
            .replace( />/g,  '&gt;' )
            .replace( /"/g,  '&quot;' )
            .replace( /'/g,  '&#39;' );
    }

} );